import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { CreditCard, PhoneCall, Coins, CircleDollarSign } from "lucide-react";
import {
  getPlanUsage,
  type PlanUsageItem,
  type PlanUsageListResponse,
} from "@/services/plansService";
import {
  PENDING_PLAN_LABEL,
  formatPlanInteger,
  formatPlanRate,
} from "@/utils/planDisplay";

interface PlanUsageCardsProps {
  month: number;
  year: number;
  clientId?: string | null;
}

function isUsageItem(
  data: PlanUsageItem | PlanUsageListResponse,
): data is PlanUsageItem {
  return "client_id" in data && "plan" in data;
}

function usagePercent(used: number, limit: number): number {
  if (!limit || limit <= 0) return 0;
  return Math.min(100, Math.round((used / limit) * 100));
}

function formatExcessCost(value: number): string {
  return Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function resolveClientParam(
  clientId?: string | null,
): string | undefined {
  if (!clientId || clientId === "all" || clientId === "null") {
    return undefined;
  }
  return clientId;
}

export const PlanUsageCards = ({
  month,
  year,
  clientId,
}: PlanUsageCardsProps) => {
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState<PlanUsageItem | null>(null);
  const [needsClientSelection, setNeedsClientSelection] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      setNeedsClientSelection(false);
      setUsage(null);

      const clientParam = resolveClientParam(clientId);

      if (!clientParam) {
        if (!cancelled) {
          setNeedsClientSelection(true);
          setLoading(false);
        }
        return;
      }

      try {
        const response = await getPlanUsage({
          month,
          year,
          client_id: clientParam,
        });

        if (cancelled) return;

        if (!response.success || !response.data) {
          setError(
            response.error?.message || "Não foi possível carregar o uso do plano",
          );
          return;
        }

        const data = response.data;

        if (isUsageItem(data)) {
          setUsage(data);
          return;
        }

        setNeedsClientSelection(true);
      } catch {
        if (!cancelled) {
          setError("Não foi possível carregar o uso do plano");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [month, year, clientId]);

  if (loading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="space-y-0 pb-2">
              <div className="h-4 w-28 bg-muted rounded" />
            </CardHeader>
            <CardContent>
              <div className="h-8 w-20 bg-muted rounded mb-2" />
              <div className="h-2 w-full bg-muted rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Uso do plano</CardTitle>
          <CardDescription>{error}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (needsClientSelection) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Uso do plano</CardTitle>
          <CardDescription>
            Selecione um cliente no filtro para acompanhar o consumo do plano.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (!usage) {
    return null;
  }

  if (!usage.plan) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle className="text-base">Uso do plano</CardTitle>
            <CardDescription>
              {usage.client.company || usage.client.name || "Cliente"}
            </CardDescription>
          </div>
          <CreditCard className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <p className="text-lg font-semibold">{PENDING_PLAN_LABEL}</p>
        </CardContent>
      </Card>
    );
  }

  const usedCalls = usage.in_plan_calls + usage.excess_calls;
  const usedTokens = usage.in_plan_tokens + usage.excess_tokens;
  const callLimit = Number(usage.plan.call_limit) || 0;
  const tokenLimit = Number(usage.plan.token_limit) || 0;
  const callsPct = usagePercent(usedCalls, callLimit);
  const tokensPct = usagePercent(usedTokens, tokenLimit);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-medium text-muted-foreground">
            Uso do plano comercial
          </h3>
          <p className="text-xs text-muted-foreground">
            {usage.client.company || usage.client.name || "Cliente"} ·{" "}
            {String(month).padStart(2, "0")}/{year}
          </p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Plano contratado</CardTitle>
            <CreditCard className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent className="space-y-1">
            <div className="text-2xl font-bold">{usage.plan.name}</div>
            <p className="text-xs text-muted-foreground">
              Limite {formatPlanInteger(callLimit)} acionamentos /{" "}
              {formatPlanInteger(tokenLimit)} tokens
            </p>
            <p className="text-xs text-muted-foreground">
              Excedente R$ {formatPlanRate(usage.plan.excess_rate_per_1m_tokens)}{" "}
              / 1M tokens
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Acionamentos</CardTitle>
            <PhoneCall className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="text-2xl font-bold">
              {formatPlanInteger(usedCalls)}
              <span className="text-sm font-normal text-muted-foreground">
                {" "}
                / {formatPlanInteger(callLimit)}
              </span>
            </div>
            <Progress value={callsPct} />
            <p className="text-xs text-muted-foreground">{callsPct}% do limite</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tokens</CardTitle>
            <Coins className="h-4 w-4 text-success" />
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="text-2xl font-bold">
              {formatPlanInteger(usedTokens)}
              <span className="text-sm font-normal text-muted-foreground">
                {" "}
                / {formatPlanInteger(tokenLimit)}
              </span>
            </div>
            <Progress value={tokensPct} />
            <p className="text-xs text-muted-foreground">{tokensPct}% do limite</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Excedente acumulado
            </CardTitle>
            <CircleDollarSign className="h-4 w-4 text-warning" />
          </CardHeader>
          <CardContent className="space-y-1">
            <div className="text-2xl font-bold">
              {formatExcessCost(usage.excess_cost)}
            </div>
            <p className="text-xs text-muted-foreground">
              {formatPlanInteger(usage.excess_tokens)} tokens excedentes · taxa
              do plano
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
